import React, { useState, useEffect, useCallback } from 'react';
import { aiTutorAPI, ComponentTrigger } from '../../services/api';

interface ComponentTriggersProps {
  componentType: string;
  triggerLocation: 'component_inline' | 'definition_item' | 'content_footer' | 'result_card';
  subject?: string;
  context?: Record<string, any>;
  // Template variables to replace in prompts (e.g., {term}, {front})
  templateVars?: Record<string, string>;
  // Callback when a trigger is clicked
  onTriggerClick: (prompt: string, sourceComponent?: { type: string; content: Record<string, any> }) => void;
  // Source component data for AI context
  sourceComponentType?: string;
  sourceComponentContent?: Record<string, any>;
  // Styling
  size?: 'small' | 'medium';
  className?: string;
  // Fallback triggers if API fails
  fallbackTriggers?: Array<{ label: string; prompt: string }>;
}

/**
 * ComponentTriggers - Renders AI trigger buttons fetched from the database.
 *
 * This component fetches configured triggers for a specific component type
 * and location, then renders them as clickable buttons.
 *
 * Features:
 * - Fetches triggers from /ai-tutor/component-triggers API
 * - Caches triggers per component type/location
 * - Supports template variables in prompts ({term}, {front}, etc.)
 * - Tracks clicks for analytics
 * - Falls back to hardcoded triggers if API fails
 */
const ComponentTriggers: React.FC<ComponentTriggersProps> = ({
  componentType,
  triggerLocation,
  subject,
  context,
  templateVars,
  onTriggerClick,
  sourceComponentType,
  sourceComponentContent,
  size = 'small',
  className = '',
  fallbackTriggers
}) => {
  const [triggers, setTriggers] = useState<ComponentTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch triggers from API
  useEffect(() => {
    const fetchTriggers = async () => {
      try {
        setLoading(true);
        const result = await aiTutorAPI.getComponentTriggers(
          componentType,
          triggerLocation,
          subject,
          context
        );
        setTriggers(result);
        setError(false);
      } catch (err) {
        console.error('Failed to fetch component triggers:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTriggers();
  }, [componentType, triggerLocation, subject]);

  // Apply template variables to prompt
  const applyTemplateVars = useCallback((prompt: string): string => {
    if (!templateVars) return prompt;

    let result = prompt;
    for (const [key, value] of Object.entries(templateVars)) {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
    return result;
  }, [templateVars]);

  // Handle trigger click
  const handleClick = async (trigger: ComponentTrigger) => {
    // Track the click (fire and forget)
    aiTutorAPI.trackTriggerClick(trigger.rule_id).catch(() => {
      // Ignore tracking errors
    });

    // Apply template variables to prompt
    const finalPrompt = applyTemplateVars(trigger.prompt);

    // Call the callback with the processed prompt
    onTriggerClick(
      finalPrompt,
      sourceComponentType ? {
        type: sourceComponentType,
        content: sourceComponentContent || {}
      } : undefined
    );
  };

  // If loading, show skeleton
  if (loading) {
    return (
      <div className={`component-triggers ${className}`}>
        <div className="trigger-chip skeleton" style={{ width: '60px', height: '24px' }} />
        <div className="trigger-chip skeleton" style={{ width: '70px', height: '24px' }} />
      </div>
    );
  }

  // If error or no triggers, use fallback
  const displayTriggers = (error || triggers.length === 0) && fallbackTriggers
    ? fallbackTriggers.map((t, i) => ({
        rule_id: `fallback-${i}`,
        label: t.label,
        prompt: t.prompt,
        category: 'clarify' as const,
        priority: 'medium' as const,
        trigger_location: triggerLocation,
        context_hint: undefined as string | undefined
      }))
    : triggers;

  if (displayTriggers.length === 0) {
    return null;
  }

  return (
    <div className={`component-triggers ${className}`}>
      {displayTriggers.map((trigger) => (
        <button
          key={trigger.rule_id}
          className={`trigger-chip ${size}`}
          onClick={() => handleClick(trigger)}
          title={trigger.context_hint || trigger.prompt}
        >
          {trigger.label}
        </button>
      ))}
    </div>
  );
};

export default ComponentTriggers;
